import React, { useState, useEffect, useRef } from 'react';
import { User } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { branding } from '@/config/branding';
import { cn, startLoginProcess, completeLoginProcess } from '@/lib/utils';
import { apiGet, apiPost } from '@/lib/api';
import { useUIStore } from '@/stores';
import { navigateTo } from '@/lib/hashRouter';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};
// Define interfaces based on the Vue code logic
interface UrlParams {
  code?: string;
  state?: string;
  session_state?: string;
  [key: string]: string | undefined;
}
// interface MicrosoftLoginData {
//   urlParams: UrlParams;
// }
interface LoginProps {
  loginUrl?: string;
  onLoginSuccess?: (token: string) => void;
  onLoginError?: (error: string) => void;
}
export default function Login({
  onLoginSuccess,
  onLoginError,
  loginUrl,
}: LoginProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [disabled, setDisabled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 使用 ref 防止重复执行 token 交换
  const tokenExchangeAttemptedRef = useRef(false);

  // Parse URL parameters to get OAuth response
  const parseUrlParams = (): UrlParams => {
    const params: UrlParams = {};
    const urlParams = new URLSearchParams(window.location.search);
    for (const [key, value] of urlParams.entries()) {
      params[key] = value;
    }
    return params;
  };
  // Exchange authorization code for token
  const exchangeCodeForToken = async (urlParams: UrlParams) => {
    try {
      setIsProcessing(true);
      // Build query string from URL parameters
      const queryParams = new URLSearchParams();
      Object.entries(urlParams).forEach(([key, value]) => {
        if (value && typeof value === 'string') {
          queryParams.append(key, value);
        }
      });
      queryParams.append('redirect_uri', location.origin + '/');
      // 先调用登录接口获取 token，这个请求不应该被阻塞
      const response = await apiGet<any>(
        `/api/oauth/microsoft_callback?${queryParams.toString()}`
      );
      const result = response?.data || response || {};
      if (result.token) {
        // Store authentication data
        localStorage.setItem('user-token', result.token || '');
        localStorage.setItem('isLoggedIn', 'true');
        // 🔥 用新 token 重新连接 socket.io（避免退出后重新登录 Authorization 为空）
        import('@/lib/socket').then(mod => mod.default.reconnect());
        // 在存储 token 之后，立即开始登录流程
        // 这样后续的 API 请求都会等待登录完成
        startLoginProcess();
        // 立即清除 URL 参数（不要等待）
        // 这样用户刷新页面也不会重复执行 token 交换
        // 注意：保留 hash 以确保路由系统正常工作
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + (window.location.hash || '#/chat')
        );

        // 同步 URL hash
        navigateTo({ type: 'chat' });

        // 使用 store 的 action 方法更新状态
        const { setCurrentView, setSelectedNavItem } = useUIStore.getState();
        setCurrentView('chat');
        setSelectedNavItem('');

        // 调用回调进行后续初始化（用户信息获取、数据加载等）
        onLoginSuccess?.(result.token);

        // 延迟释放登录流程，给初始 API 调用足够的时间完成
        setTimeout(() => {
          completeLoginProcess();
        }, 2000); // 2秒后释放，确保初始数据加载完成
        return result;
      } else {
        throw new Error(result.message || t('errors.failedToGetToken'));
      }
    } catch (error) {
      console.error('Token exchange failed:', error);
      const errorMessage =
        error instanceof Error ? error.message : t('errors.loginFailed');
      setError(errorMessage);
      onLoginError?.(errorMessage);
      // 登录失败时立即释放登录流程（如果已经开始）
      completeLoginProcess();
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };
  // Handle OAuth redirect on component mount
  useEffect(() => {
    // 如果已经尝试过 token 交换，不要重复执行
    if (tokenExchangeAttemptedRef.current) {
      return;
    }
    const urlParams = parseUrlParams();
    // Check for OAuth errors first
    if (urlParams.error) {
      const errorMessage =
        urlParams.error_description ||
        urlParams.error ||
        t('errors.oauthError');
      console.error('OAuth error:', errorMessage);
      setError(errorMessage);
      onLoginError?.(errorMessage);
      tokenExchangeAttemptedRef.current = true;
      return;
    }
    // Check if we have an authorization code from OAuth redirect
    if (urlParams.code) {
      tokenExchangeAttemptedRef.current = true; // 标记为已尝试
      setError(null); // Clear any previous errors
      exchangeCodeForToken(urlParams)
        .then(() => {
          // Login successful, the onLoginSuccess callback will handle state updates
        })
        .catch(error => {
          console.error('Login process failed:', error);
          // Stay on login page to allow retry
          // 重置标记，允许用户重试
          tokenExchangeAttemptedRef.current = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initiate Microsoft OAuth login
  const login = async () => {
    if (window.Q2Portal?.isElectron) {
      setIsProcessing(true);
      setError(null);
      try {
        const userInfo: any = await window.Q2Portal.getUserInfo();
        if (userInfo?.username) {
          // 先调用登录接口，不要阻塞这个请求
          const response = await apiPost<any>(`/api/trade_token_by_username`, {
            username: userInfo?.username,
          });
          const result = response?.data || response || {};
          if (result.token) {
            // Store authentication data
            localStorage.setItem('user-token', result.token || '');
            localStorage.setItem('isLoggedIn', 'true');
            // Electron 登录使用 reload，登录流程会在新页面重新开始
            // 所以这里可以直接 reload，不需要 startLoginProcess/completeLoginProcess
            location.reload();
            return result;
          } else {
            const errorMessage = result.message || t('errors.failedToGetToken');
            setError(errorMessage);
            onLoginError?.(errorMessage);
          }
        }
      } catch (error) {
        console.error('Electron login failed:', error);
        const errorMessage =
          error instanceof Error ? error.message : t('errors.loginFailed');
        setError(errorMessage);
        onLoginError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    } else {
      if (!loginUrl) {
        const errorMessage = t('errors.loginUrlNotAvailable');
        setError(errorMessage);
        onLoginError?.(errorMessage);
        return;
      }
      setDisabled(true);
      setError(null); // Clear any previous errors
      try {
        // Redirect to Microsoft OAuth
        const urlObj = new URL(loginUrl);
        urlObj.searchParams.set('redirect_uri', window.location.origin + '/');
        window.location.replace(urlObj.toString());
      } catch (error) {
        console.error('Failed to initiate login:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('errors.failedToStartLogin');
        setError(errorMessage);
        onLoginError?.(errorMessage);
        setDisabled(false);
      }
    }
  };
  return (
    <div className="flex h-full bg-gray-50">
      <div className="flex-1 flex flex-col justify-center items-center p-6">
        {/* Claude Logo */}
        <div className="mb-6 sm:mb-12">
          <div className="flex items-center gap-3">
            <div className="flex flex-start shrink-0 gap-2 py-3">
              <img
                src={branding.logo}
                alt={branding.name}
                style={{
                  width: branding.welcomeLogoStyle.width,
                  height: branding.welcomeLogoStyle.height,
                }}
                className={branding.welcomeLogoStyle.className}
              />
            </div>
            <span
              className="text-2xl sm:text-3xl font-normal text-gray-800"
              style={branding.logoTextStyle}
            >
              {branding.logoText || branding.name}
            </span>
          </div>
        </div>
        {/* Main heading */}
        <div className="text-center mb-6 sm:mb-12 px-4 sm:px-0">
          <h1 className="text-2xl sm:text-4xl md:text-6xl font-normal text-gray-800 mb-3 sm:mb-4 leading-tight max-w-[30rem] mx-auto">
            {t('login.title')
              .split('。')
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index === 0 && <br />}
                </React.Fragment>
              ))}
          </h1>
          <p className="text-sm sm:text-base md:text-xl text-gray-600">
            {t('login.subtitle')}
          </p>
        </div>
        {/* Login form container */}
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-8">
            {/* Show processing state if handling OAuth redirect */}
            {isProcessing && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-blue-800 text-sm font-medium">
                    {t('login.processingLogin')}
                  </span>
                </div>
              </div>
            )}
            {/* Show error message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">!</span>
                  </div>
                  <span className="text-red-800 text-sm font-medium">
                    {error}
                  </span>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
                >
                  {t('login.tryAgain')}
                </button>
              </div>
            )}
            {/* Login button */}
            <button
              onClick={login}
              disabled={disabled || isProcessing}
              className={cn(
                'w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl border text-sm sm:text-lg font-medium transition-all duration-200',
                disabled || isProcessing
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50 hover:border-gray-400 shadow-sm hover:shadow'
              )}
            >
              {/* User Icon */}
              {window.Q2Portal?.isElectron && (
                <div className="flex items-center gap-3">
                  <User size={24} className="text-gray-600" />
                  <span>
                    {isProcessing
                      ? t('login.processingLogin')
                      : disabled
                        ? t('login.signingIn')
                        : t('login.loginWithComputer')}
                  </span>
                </div>
              )}
              {!window.Q2Portal?.isElectron && (
                <div className="flex items-center gap-3">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M11.4 11.4H2V2h9.4v9.4z" fill="#F25022" />
                    <path d="M22 11.4h-9.4V2H22v9.4z" fill="#00A4EF" />
                    <path d="M11.4 22H2v-9.4h9.4V22z" fill="#FFB900" />
                    <path d="M22 22h-9.4v-9.4H22V22z" fill="#7FBA00" />
                  </svg>
                  <span>
                    {isProcessing
                      ? t('login.processingLogin')
                      : disabled
                        ? t('login.signingIn')
                        : t('login.loginWithMicrosoft')}
                  </span>
                </div>
              )}
            </button>
            {/* Privacy notice */}
            <div className="mt-4 sm:mt-6 text-center">
              <p className="text-xs sm:text-sm text-gray-500">
                <Trans
                  i18nKey="login.privacyNotice"
                  components={{
                    privacyLink: (
                      <a
                        href="/privacy-policy.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      />
                    ),
                  }}
                />
              </p>
            </div>
          </div>
        </div>
        {/* Learn more button */}
        {/* <div className="mt-8">
          <button className="px-6 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-full hover:border-gray-400 transition-colors">
            Learn more ↓
          </button>
        </div> */}
      </div>
      {/* Full screen loading overlay */}
      {(isProcessing || disabled) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-6 shadow-2xl max-w-sm mx-4">
            {/* Large spinner */}
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
            </div>
            {/* Loading text */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {window.Q2Portal?.isElectron
                  ? t('login.authenticating')
                  : t('login.redirectingToLogin')}
              </h3>
              <p className="text-sm text-gray-600">
                {window.Q2Portal?.isElectron
                  ? t('login.verifyingCredentials')
                  : t('login.redirectingToMicrosoft')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
